import { Search } from "lucide-react";

import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";

type VoicemailDirectionFilter = "all" | "inbound" | "outbound";
type VoicemailStatusFilter = "all" | "listened" | "unlistened";

interface VoicemailFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  directionFilter: VoicemailDirectionFilter;
  onDirectionChange: (direction: VoicemailDirectionFilter) => void;
  statusFilter: VoicemailStatusFilter;
  onStatusChange: (status: VoicemailStatusFilter) => void;
}

export const VoicemailFilters: React.FC<VoicemailFiltersProps> = function({
  searchQuery,
  onSearchChange,
  directionFilter,
  onDirectionChange,
  statusFilter,
  onStatusChange,
}) {
  const directions: VoicemailDirectionFilter[] = ["all", "inbound", "outbound"];
  const statuses: VoicemailStatusFilter[] = ["all", "listened", "unlistened"];

  return (
    <div className="space-y-4">
      {/* Search bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          className="pl-10 bg-muted/50"
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search phone number, contact name, or transcript..."
          value={searchQuery}
        />
      </div>

      {/* Filter buttons - combined into single row */}
      <div className="flex flex-wrap gap-1">
        {/* All filter - resets both direction and status */}
        <Button
          onClick={() => {
            onDirectionChange("all");
            onStatusChange("all");
          }}
          size="sm"
          variant={directionFilter === "all" && statusFilter === "all" ? "default" : "outline"}
          className={
            directionFilter === "all" && statusFilter === "all"
              ? "bg-primary text-primary-foreground"
              : ""
          }
        >
          All
        </Button>

        {/* Direction filters (excluding "all") */}
        <Button
          onClick={() => onDirectionChange("inbound")}
          size="sm"
          variant={directionFilter === "inbound" ? "default" : "outline"}
          className={
            directionFilter === "inbound"
              ? "bg-blue-600 hover:bg-blue-700 text-white"
              : ""
          }
        >
          Received
        </Button>
        <Button
          onClick={() => onDirectionChange("outbound")}
          size="sm"
          variant={directionFilter === "outbound" ? "default" : "outline"}
          className={
            directionFilter === "outbound"
              ? "bg-green-600 hover:bg-green-700 text-white"
              : ""
          }
        >
          Sent
        </Button>

        {/* Separator */}
        <div className="w-px bg-border mx-1" />

        {/* Status filters (excluding "all") */}
        {statuses.filter(s => s !== "all").map((status) => (
          <Button
            key={status}
            onClick={() => onStatusChange(status)}
            size="sm"
            variant={statusFilter === status ? "default" : "outline"}
            className={
              statusFilter === status
                ? "bg-primary text-primary-foreground"
                : ""
            }
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Button>
        ))}
      </div>
    </div>
  );
}
